<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { debounce } from "lodash";
import moment from "moment-timezone";

import { ModalName } from '@/constants/modals';
import { toManageOrderMissionForm } from "@/modules/companyNeed/helpers/manageOrderModal";
import { dateRangeToPeriod, missionPeriodToDateRange, toKnownMissionStatus } from "@/modules/companyNeed/helpers/mission";
import { createTempId } from "@/modules/companyNeed/helpers/tempId";
import missionService from "@/modules/companyNeed/services/missionService";
import { useCompanyNeedStore } from "@/modules/companyNeed/store/companyNeedStore";
import { type MissionList, MissionState, MissionStatus } from "@/modules/companyNeed/views/details/missions/types";
import workerService from "@/modules/worker/services/workerService";
import { useConfigurationStore } from "@/store/configurationStore";
import { WorkerMissionStatus } from '@/types/mission';

import type { DateRange } from "@/components/Mission/DateRangeTable/types";
import type { TableAltRows } from "@/dsComponents/TableAlt/types";
import type { MissionModalData, MissionWorker } from "@/modules/companyNeed/types/missions";

import { AvatarSize } from "@/dsComponents/Avatar";
import Avatar from "@/dsComponents/Avatar/Avatar.vue";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Link from "@/dsComponents/buttons/Link.vue";
import Checkbox from "@/dsComponents/checkbox/Checkbox.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import ModalSection from "@/dsComponents/modals/ModalSection.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import TableCell from "@/dsComponents/Table/TableCell.vue";
import { TableFontColor } from "@/dsComponents/Table/types";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

import DateRangeTable from "@/components/Mission/DateRangeTable/DateRangeTable.vue";
import { useDateRange } from "@/components/Mission/DateRangeTable/useDateRange";

interface PlanningSlot {
    available: boolean;
}

interface PlanningDay {
    date: number;
    morning?: PlanningSlot;
    afternoon?: PlanningSlot;
    evening?: PlanningSlot;
    night?: PlanningSlot;
}

interface Props {
    open: boolean;
    missionId: string;
    /** Receives the mission created by the extension or the duplication, as known from the command-side. */
    onMissionCreated?: (createdMission: MissionList) => void;
}

const props = defineProps<Props>();

const emit = defineEmits<(e: "close") => void>();

const { t } = useI18n();
const toaster = useToast();
const companyNeedStore = useCompanyNeedStore();
const configStore = useConfigurationStore();
const modalStore = useModalStore();

const isLoading = ref(false);
const missionData = ref<MissionModalData | null>(null);
const description = ref("");
const note = ref("");
const detailOpen = ref(false);
const selectedWorkerIds = ref<string[]>([]);

const { dateRanges, validate } = useDateRange();

const confirmedWorkers = computed<MissionWorker[]>(() => {
    if (!missionData.value) return [];
    return missionData.value.workersSelection.find((group) => group.workerStatus === WorkerMissionStatus.CONFIRMED)?.workers ?? [];
});

const toggleWorker = (index: number) => {
    const worker = confirmedWorkers.value[index];
    if (!worker) return;
    const existingIndex = selectedWorkerIds.value.indexOf(worker.id);
    if (existingIndex >= 0) {
        selectedWorkerIds.value.splice(existingIndex, 1);
    } else {
        selectedWorkerIds.value.push(worker.id);
    }
};

// Debounced to prevent double-firing from both cell action and row click events
const handleWorkerToggle = debounce(toggleWorker, 30);

const hiringReasonLabel = computed(() => {
    if (!missionData.value?.hiringReason) return undefined;
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    return configStore.hiringReasonOptions.find((option: { value: string }) => option.value === missionData.value!.hiringReason)?.label;
});

const formattedRemuneration = computed(() => {
    const remuneration = missionData.value?.remuneration;
    if (!remuneration?.minimumAmount || !remuneration?.maximumAmount) return undefined;
    const minAmount = remuneration.minimumAmount.toString();
    const maxAmount = remuneration.maximumAmount.toString();
    return t("modals.missionDetails.details.remuneration.value", { minAmount, maxAmount, periodicity: remuneration.periodicity });
});

const workerColumns = [{ field: "checkbox", width: "30px" }, { field: "profil" }, { field: "availability" }];

const workerUnavailableDays = ref<Record<string, number[]>>({});

const completeDateRanges = computed(() => dateRanges.value.filter((range) => range.startDate != null && range.endDate != null));

const isDayWithinRanges = (day: number) =>
    completeDateRanges.value.some((range) =>
        moment(day).isBetween(moment(range.startDate).startOf("day"), moment(range.endDate).endOf("day"), "day", "[]")
    );

const fetchWorkersAvailability = async () => {
    if (!confirmedWorkers.value.length || !completeDateRanges.value.length) {
        workerUnavailableDays.value = {};
        return;
    }

    const startDate = Math.min(...completeDateRanges.value.map((range) => range.startDate as number));
    const endDate = Math.max(...completeDateRanges.value.map((range) => range.endDate as number));

    const entries = await Promise.all(
        confirmedWorkers.value.map(async (worker): Promise<[string, number[]]> => {
            const planning = (await workerService
                .getWorkerPlanning(worker.id, startDate, endDate)
                .catch(() => ({ days: [] }))) as { days?: PlanningDay[] };

            const unavailableDays = (planning.days ?? [])
                .filter((day) => isDayWithinRanges(day.date))
                .filter((day) => [day.morning, day.afternoon, day.evening, day.night].some((slot) => slot && !slot.available))
                .map((day) => day.date)
                .sort((left, right) => left - right);

            return [worker.id, unavailableDays];
        })
    );

    workerUnavailableDays.value = Object.fromEntries(entries);
};

// Debounced: period edits fire on every date/time change in the range table
const debouncedFetchWorkersAvailability = debounce(fetchWorkersAvailability, 400);

const availabilityCell = (workerId: string) => {
    const unavailableDays = workerUnavailableDays.value[workerId];
    if (!unavailableDays?.length) return null;

    const firstDate = moment(unavailableDays[0]).format("DD MMM");
    const cellTitle =
        unavailableDays.length > 1
            ? t("modals.extendMission.unavailability.multiple", { date: firstDate, count: unavailableDays.length - 1 })
            : t("modals.extendMission.unavailability.single", { date: firstDate });

    return {
        cellTitle,
        fontType: "passive",
        fontColor: TableFontColor.red,
        icon: { iconType: "cal-x", iconColor: "red" },
    };
};

const workersFormatted = computed<TableAltRows[]>(() =>
    confirmedWorkers.value.map((worker, index) => {
        const isSelected = selectedWorkerIds.value.includes(worker.id);
        const unavailabilityCell = availabilityCell(worker.id);
        return {
            active: isSelected,
            content: {
                checkbox: {
                    customElement: {
                        component: Checkbox,
                        props: { checked: isSelected, size: "sm" },
                        action: () => handleWorkerToggle(index),
                    },
                },
                profil: {
                    customElement: {
                        component: Avatar,
                        props: {
                            firstname: worker.firstname,
                            lastname: worker.lastname,
                            fontSize: "12px",
                            size: AvatarSize.size16,
                            displayName: true,
                        },
                    },
                },
                availability: unavailabilityCell
                    ? {
                          customElement: {
                              component: TableCell,
                              props: unavailabilityCell,
                          },
                      }
                    : {},
            },
        };
    })
);

const initializeDateRange = () => {
    if (!missionData.value?.periods.length) return;
    const lastPeriod = missionData.value.periods[missionData.value.periods.length - 1];
    const lastPeriodEnd = moment(lastPeriod.end);
    const isFriday = lastPeriodEnd.isoWeekday() === 5;
    const extendStart = isFriday
        ? lastPeriodEnd.clone().add(3, "days").startOf("day").valueOf()
        : lastPeriodEnd.clone().add(1, "day").startOf("day").valueOf();
    const extendEnd = moment(extendStart).add(7, "days").endOf("day").valueOf();
    const { startTime, endTime } = missionPeriodToDateRange(lastPeriod);

    dateRanges.value = [
        {
            startDate: extendStart,
            endDate: extendEnd,
            startTime,
            endTime,
            weekendIncluded: false,
        },
    ];
};

const handleChangePeriod = (newDateRanges: DateRange[]) => {
    dateRanges.value = newDateRanges;
    debouncedFetchWorkersAvailability();
};

const handleClose = () => {
    emit("close");
};

const handleDuplicate = () => {
    const mission = missionData.value;
    if (!mission) return;

    modalStore.actions.open(ModalName.ManageOrderModal, {
        besoinId: mission.besoinId,
        companyId: mission.companyId,
        jobId: mission.jobId,
        isDuplicate: true,
        mission: {
            ...toManageOrderMissionForm(mission),
            onSuccess: (createdMission?: MissionList) => {
                if (createdMission) props.onMissionCreated?.(createdMission);
            },
        },
    });
    handleClose();
};

// The extension is a new mission: the source one with the new periods and the selected
// workers, which are confirmed ones only
const buildOptimisticExtension = (mission: MissionModalData): MissionList => {
    const confirmedCount = selectedWorkerIds.value.length;
    return {
        id: createTempId(),
        status: MissionStatus.TO_PROCESS,
        name: mission.name,
        totalPositions: mission.totalPositions,
        periods: dateRanges.value.map(dateRangeToPeriod).map((period) => ({
            start: Date.parse(period.start),
            end: Date.parse(period.end),
        })),
        city: mission.address?.city ?? "",
        contractType: mission.contractType,
        states: [MissionState.EXTENDED],
        availableActions: [],
        confirmedWorkers: confirmedCount,
        workers: [],
        workersSelection: confirmedCount > 0 ? [{ workerStatus: WorkerMissionStatus.CONFIRMED, quantity: confirmedCount }] : [],
    };
};

const handleSubmit = async () => {
    const mission = missionData.value;
    if (!mission) return;

    if (selectedWorkerIds.value.length === 0) {
        toaster?.open({ message: t("modals.extendMission.error.noWorkers"), type: "error", duration: 3000 });
        return;
    }

    if (dateRanges.value.length === 0) {
        toaster?.open({ message: t("page.order.error.startDate"), type: "error", duration: 3000 });
        return;
    }

    if (!validate(true)) return;

    // The detail store only holds the missions of the company need currently displayed
    const isDisplayedCompanyNeed = companyNeedStore.companyNeedId === mission.besoinId;
    const optimisticMission = buildOptimisticExtension(mission);
    if (isDisplayedCompanyNeed) companyNeedStore.addOptimisticMission(optimisticMission);

    isLoading.value = true;
    try {
        const createdOrder = await missionService.extendMission(props.missionId, {
            besoinId: mission.besoinId,
            companyId: mission.companyId,
            jobId: mission.jobId,
            contractType: mission.contractType,
            totalPositions: mission.totalPositions,
            periods: dateRanges.value.map(dateRangeToPeriod),
            address: mission.address,
            name: mission.name,
            hiringReason: mission.hiringReason,
            justification: mission.explanation,
            description: description.value || undefined,
            note: note.value || undefined,
            workerIds: [...selectedWorkerIds.value],
        });

        toaster?.open({ message: t("modals.extendMission.success"), type: "success", duration: 5000 });
        if (createdOrder?.id) {
            const createdMission: MissionList = {
                ...optimisticMission,
                id: createdOrder.id,
                status: toKnownMissionStatus(createdOrder.status, optimisticMission.status),
            };
            companyNeedStore.reconcileOptimisticMission(optimisticMission.id, {
                id: createdMission.id,
                status: createdMission.status,
            });
            if (isDisplayedCompanyNeed) void companyNeedStore.fillCreatedMissionActions(createdMission.id);
            props.onMissionCreated?.(createdMission);
        } else {
            companyNeedStore.removeOptimisticMission(optimisticMission.id);
        }
        handleClose();
    } catch {
        companyNeedStore.removeOptimisticMission(optimisticMission.id);
        toaster?.open({ message: t("errors.Global.ServerError._default"), type: "error", duration: 5000 });
    } finally {
        isLoading.value = false;
    }
};

const fetchMissionData = async () => {
    isLoading.value = true;
    try {
        missionData.value = await missionService.fetchMissionDetails(props.missionId);
        description.value = missionData.value.description ?? "";
        note.value = missionData.value.note ?? "";
        initializeDateRange();
        selectedWorkerIds.value = confirmedWorkers.value.map((worker) => worker.id);
        fetchWorkersAvailability();
    } catch {
        toaster?.open({ message: t("errors.Global.ServerError._default"), type: "error", duration: 5000 });
    } finally {
        isLoading.value = false;
    }
};

onMounted(fetchMissionData);
</script>

<template>
    <Modal :open="open" width="720px" :title="t('modals.extendMission.title')" close @close="handleClose">
        <div v-if="isLoading" class="mission-extension-modal__loading typo-sm-regular">
            {{ t("global.loading") }}
        </div>

        <div v-else-if="missionData" class="mission-extension-modal">
            <TextInputAlt :model-value="missionData.name" variant="transparent" disabled />

            <Link
                type="neutral"
                size="xs"
                :class="['details-toggle', { open: detailOpen }]"
                @click="detailOpen = !detailOpen"
                right-icon="caret-right-filled"
            >
                <span>{{ t("page.order.missionDetails") }}</span>
            </Link>

            <div :class="['detail-section', { open: detailOpen }]">
                <template v-if="missionData.address.city">
                    <div class="detail-section__label typo-xxs-medium">
                        <i class="ti ti-map-pin-circle typo-sm-regular" />
                        <span>{{ t("modals.missionDetails.details.label.location") }}</span>
                    </div>
                    <ComboButton :label="missionData.address.city" readonly />
                </template>

                <template v-if="missionData.contractType">
                    <div class="detail-section__label typo-xxs-medium">
                        <i class="ti ti-signature typo-sm-regular" />
                        <span>{{ t("page.order.contract") }}</span>
                    </div>
                    <ComboButton :label="missionData.contractType" readonly />
                </template>

                <template v-if="missionData.totalPositions">
                    <div class="detail-section__label typo-xxs-medium">
                        <i class="ti ti-users typo-sm-regular" />
                        <span>{{ t("page.order.quantity") }}</span>
                    </div>
                    <ComboButton :label="String(missionData.totalPositions)" readonly />
                </template>

                <template v-if="formattedRemuneration">
                    <div class="detail-section__label typo-xxs-medium">
                        <i class="ti ti-cash typo-sm-regular" />
                        <span>{{ t("modals.missionDetails.details.remuneration.label") }}</span>
                    </div>
                    <ComboButton :label="formattedRemuneration" readonly />
                </template>

                <template v-if="hiringReasonLabel">
                    <div class="detail-section__label typo-xxs-medium">
                        <i class="ti ti-selector typo-sm-regular" />
                        <span>{{ t("page.order.reason") }}</span>
                    </div>
                    <ComboButton :label="hiringReasonLabel" readonly />
                </template>

                <template v-if="missionData.explanation">
                    <div class="detail-section__label typo-xxs-medium">
                        <i class="ti ti-answer-text typo-sm-regular" />
                        <span>{{ t("page.order.justification") }}</span>
                    </div>
                    <ComboButton :label="missionData.explanation" readonly />
                </template>
            </div>

            <ModalSection :title="t('page.order.dates')" />
            <DateRangeTable v-if="dateRanges.length" :date-ranges="dateRanges" @ranges="handleChangePeriod" />

            <ModalSection :title="t('page.order.workerToExtend')" />
            <TableAlt
                v-if="workersFormatted.length > 0"
                :rows="workersFormatted"
                :columns="workerColumns"
                row-click
                @row-click="handleWorkerToggle"
            />
            <p v-else class="mission-extension-modal__no-workers typo-sm-regular">
                {{ t("modals.extendMission.error.noWorkers") }}
            </p>

            <ModalSection :title="t('page.order.description')" />
            <div class="mission-extension-modal__description">
                <TextInputAlt variant="transparent" :placeholder="t('page.order.descriptionPlaceholder')" v-model="description" />
                <div class="mission-extension-modal__note-label typo-xxs-semibold">{{ t("page.order.addNote.label") }}</div>
                <TextInputAlt variant="transparent" :placeholder="t('page.order.addNote.placeholder')" v-model="note" />
            </div>
        </div>

        <template #footer>
            <div class="mission-extension-modal__footer">
                <ButtonAlt variant="stroke" type="neutral" left-icon="copy" :disabled="isLoading" @click="handleDuplicate">
                    {{ t("modals.extendMission.duplicate") }}
                </ButtonAlt>
                <div class="mission-extension-modal__footer-actions">
                    <ButtonAlt variant="ghost" type="neutral" @click="handleClose">{{ t("global.actions.cancel") }}</ButtonAlt>
                    <ButtonAlt variant="filled" :disabled="isLoading" @click="handleSubmit">
                        {{ t("modals.extendMission.submit") }}
                    </ButtonAlt>
                </div>
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.mission-extension-modal {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3xl, 24px);

    &__loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--spacing-6xl, 48px);
        color: var(--text-sub-600, #4c505e);
    }

    &__no-workers {
        color: var(--text-sub-600, #4c505e);
    }

    &__description {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs, 4px);
    }

    &__note-label {
        margin-top: var(--spacing-md, 8px);
    }

    &__footer {
        display: flex;
        justify-content: space-between;
        gap: var(--spacing-lg, 12px);
    }

    &__footer-actions {
        display: flex;
        gap: var(--spacing-lg, 12px);
    }
}

.details-toggle {
    display: flex;
    align-items: center;
    background: none;
    padding: 0;

    &:hover {
        color: $text-sub-600;
    }

    :deep(.ti) {
        transition: 0.2s;
        transform: rotate(0);
    }

    &.open :deep(.ti) {
        transform: rotate(90deg);
    }
}

.detail-section {
    display: none;
    grid-template-columns: 120px 1fr;
    gap: var(--spacing-md, 8px);

    &.open {
        display: grid;
    }

    & > * {
        min-height: 28px;
        display: flex;
        align-items: center;
    }

    &__label {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm, 6px);
        white-space: nowrap;
        color: var(--text-sub-600, #494e61);

        .ti {
            color: var(--icon-sub-500, #636778);
        }
    }
}
</style>
